/**
 * solRegions.ts - what is on the photosphere at a given instant.
 *
 * `tools/atlas/bake_sol_map.py` writes ONE Carrington rotation as imagery and
 * the registry stamps its date; this is the other half, the catalogue that map
 * is a picture of. `tools/atlas/bake_sol_regions.py` turns the RGO/USAF-NOAA
 * sunspot group record (1874-05-01 to 2016-12-31) into
 * `public/art/atlas/sol-regions.json`, and this reads it: given a Julian date,
 * the active regions on the WHOLE sphere at that instant, each one carried in
 * the Carrington frame by differential rotation at its own latitude, its area
 * grown and decayed across its observed life, and flagged measured or
 * artificial.
 *
 * Pure: no DOM, no three.js, no `import.meta`. It is a LEAF on purpose -
 * anything reaching `dtmBase.ts` pulls in `import.meta.env` and stops a Node
 * import, and `tools/preview/live-sun-check.mjs` imports this directly.
 *
 * Same date in, byte-identical list out. The artificial regions are drawn from
 * a seeded generator keyed by CYCLE NUMBER, never by the query date, so a date
 * a second later gets the same regions one second older rather than a new roll.
 */

/** Degrees to radians. */
const DEG = Math.PI / 180;

/**
 * Where a region's area is ramped to nothing at each end of its observed life.
 *
 * The record is daily rows, so a region's first and last row are a step from
 * and to nothing. This is the one piece of the measured regions that is
 * modelled: half a day of ramp either side, which is under the sampling
 * interval and is what keeps a spot from popping into existence at full size.
 */
const EDGE_RAMP_DAYS = 0.5;

/** A granulation cell's lifetime, seconds; the rate the field renews at. */
export const GRANULE_LIFETIME_SECONDS = 600;

/** Typical granule diameter, kilometres; a cell size, not a texture size. */
export const GRANULE_DIAMETER_KM = 1000;

/**
 * Limb darkening, the quadratic law already drawn in `sol.ts`.
 *
 * Kept here so the shader stage reads one number: I(mu)/I(0) = 1 - u1 (1 - mu)
 * - u2 (1 - mu)^2. Nearest published pair is Claret (2000, A&A 363, 1081),
 * ATLAS quadratic grid at Teff 5750 K / log g 4.50, Johnson V: (0.437, 0.295).
 */
export const LIMB_DARKENING_U1 = 0.47;
export const LIMB_DARKENING_U2 = 0.23;

/**
 * Where facular contrast peaks, in mu.
 *
 * Faculae are invisible at disc centre and brighten toward the limb, turning
 * over in the last few percent of the radius. Ortiz et al. (2002, A&A 388,
 * 1036) put the peak near mu 0.2 to 0.3 from MDI continuum. The SHAPE below is
 * modelled, not fitted to anything: mu^3 (1 - mu)^7, whose peak is at 0.3 by
 * construction, normalised to 1 there.
 */
export const FACULA_MU_PEAK = 0.3;

/**
 * Plage area as a multiple of the spot area of the same region.
 *
 * Modelled, not from the record - the record counts spots and says nothing
 * about the surrounding faculae. An order of magnitude is the usual quoted
 * ratio; the shader stage owns the number and this is only its default.
 */
export const PLAGE_AREA_RATIO = 10;

/**
 * Where the catalogue's two files are served from, relative to the site root.
 *
 * `SOL_MODEL_PATH` is the rotation law, the cycle model and the record's own
 * date range - a few kilobytes, fetched the moment a player opens Sol.
 * `SOL_REGIONS_PATH` is the record's own rows, 3.5+ MB, fetched only once the
 * game clock is somewhere inside the model's own recorded range.
 */
export const SOL_MODEL_PATH = 'art/atlas/sol-model.json';
export const SOL_REGIONS_PATH = 'art/atlas/sol-regions.json';

/** The differential rotation law and the frame it is written in. */
export interface SolRotation {
  /** Sidereal deg/day at the equator. */
  readonly a: number;
  /** Sidereal deg/day per sin^2(latitude); negative, the poles are slower. */
  readonly b: number;
  /** Carrington's own sidereal rate, the frame the catalogue's longitudes use. */
  readonly carringtonSidereal: number;
  /** Carrington's defining synodic period, days. */
  readonly carringtonSynodicDays: number;
  /** Earth's mean heliocentric motion, deg/day. */
  readonly earthMeanMotion: number;
  /** Anchor for the sub-Earth Carrington longitude: JD and its value there. */
  readonly l0RefJd: number;
  readonly l0RefDeg: number;
  /** Rate of the sub-Earth Carrington longitude, deg/day; negative. */
  readonly l0RatePerDay: number;
  /** Umbral share of a whole spot group's area, fitted from the record. */
  readonly umbraFraction: number;
}

/** The seeded generator's coefficients, every one fitted from the record. */
export interface SolCycleModel {
  /** JD of each cycle minimum the record's own smoothed area finds. */
  readonly minimaJd: readonly number[];
  /** Cycle number of the first minimum: 12, October 1878. */
  readonly firstCycleNumber: number;
  readonly meanCycleDays: number;
  readonly regionsPerCycleMean: number;
  readonly regionsPerCycleSd: number;
  /** Share of a cycle's regions emerging in each tenth of its phase. */
  readonly emergenceByPhase: readonly number[];
  /** Mean and spread of absolute emergence latitude per tenth of phase. */
  readonly butterflyLatDeg: readonly number[];
  readonly butterflyLatSdDeg: readonly number[];
  /** Peak area is log-normal: these are the natural-log mean, sd and cap. */
  readonly peakAreaLnMu: number;
  readonly peakAreaLnSd: number;
  readonly peakAreaMax: number;
  /** ln(observed span days) = a + b ln(peak area), with this residual sd. */
  readonly lifetimeLnA: number;
  readonly lifetimeLnB: number;
  readonly lifetimeLnSd: number;
  /** Median area over peak at eleven normalised times, rescaled to peak at 1. */
  readonly profile: readonly number[];
}

/** One region as the catalogue stores it: a track, not an instant. */
interface CatalogueRegion {
  readonly jd0: number;
  readonly jdPeak: number;
  readonly jd1: number;
  readonly latDeg: number;
  readonly lonDeg: number;
  readonly peakArea: number;
  readonly classCode: string;
  readonly number: number;
  readonly year: number;
  readonly sample0: number;
  readonly samples: number;
}

export interface SolCatalogue {
  readonly source: SolSource;
  readonly rotation: SolRotation;
  readonly model: SolCycleModel;
  /** First and last JD the record covers, at noon of those days. */
  readonly recordStartJd: number;
  readonly recordEndJd: number;
  /** Days inside the record that nobody observed, as JD at noon. */
  readonly unobservedJd: ReadonlySet<number>;
  /** Regions in ascending order of first observation. Empty until the record loads. */
  readonly regions: readonly CatalogueRegion[];
  /** JD of each daily sample, flat, indexed by a region's sample0 and count. */
  readonly sampleJd: Float64Array;
  readonly sampleArea: Float64Array;
  /** Longest observed life in the record, days; the query's lookback. */
  readonly maxLifeDays: number;
  /**
   * False until `sol-regions.json` has been merged in.
   *
   * Every date is treated as outside the record while this is false, so the
   * disc always has a Sun to draw - a seeded model one - rather than a blank
   * one while the 3.5+ MB record is still in flight or never asked for.
   */
  readonly recordLoaded: boolean;
}

export interface SolSource {
  readonly name: string;
  readonly url: string;
  readonly rights: string;
  readonly range: string;
  readonly retrieved: string;
}

/** One active region at one instant. */
export interface SolRegion {
  /** Stable across every query: `noaa-2003-10486`, `rgo-1947-14886`, `model-25-417`. */
  readonly id: string;
  /** True when this region is a row of the record, false when it is modelled. */
  readonly measured: boolean;
  /** The catalogue number, or the index within its modelled cycle. */
  readonly number: number;
  /** Heliographic latitude, degrees, positive north. */
  readonly latDeg: number;
  /** Carrington longitude at this instant, degrees, 0 to 360. */
  readonly lonDeg: number;
  /** Whole-spot area corrected for foreshortening, millionths of a hemisphere. */
  readonly areaMillionths: number;
  /** The same region's peak area over its whole life. */
  readonly peakAreaMillionths: number;
  /** Angular radius of the equivalent circular spot group, degrees. */
  readonly radiusDeg: number;
  /** Angular radius of the umbra inside that, degrees. */
  readonly umbraRadiusDeg: number;
  /** The record's group type, `BGD` and the rest; `z<n>` before 1982, '' if none. */
  readonly classCode: string;
  /** Days since the region's first appearance. */
  readonly ageDays: number;
  /** Days from first appearance to last. */
  readonly lifetimeDays: number;
}

export interface SolCycleState {
  /** Solar cycle number, 12 upward. */
  readonly number: number;
  /** 0 at the opening minimum, 1 at the next. */
  readonly phase: number;
  readonly startJd: number;
  readonly lengthDays: number;
  /** False once the cycle is past the record and its length is the mean. */
  readonly measured: boolean;
}

export interface SolPhotosphere {
  readonly jd: number;
  /** Every region on the whole sphere, sorted by id. */
  readonly regions: readonly SolRegion[];
  /** True when the instant falls inside the observed record. */
  readonly inRecord: boolean;
  /** False on a day inside the record that nobody observed. */
  readonly observed: boolean;
  readonly measuredCount: number;
  readonly artificialCount: number;
  /** Share of the total spot area that is modelled, 0 to 1. */
  readonly artificialAreaFraction: number;
  readonly totalAreaMillionths: number;
  /** Carrington longitude under the sub-Earth point, degrees. */
  readonly subEarthLonDeg: number;
  readonly cycle: SolCycleState;
  /** What the player reads under the Layers note; '' when all of it is measured. */
  readonly note: string;
}

/** Sidereal rotation rate at a heliographic latitude, deg/day. */
export function siderealRateDegPerDay(rot: SolRotation, latDeg: number): number {
  const s = Math.sin(latDeg * DEG);
  return rot.a + rot.b * s * s;
}

/** Drift against the Carrington frame at a latitude, deg/day. */
export function carringtonDriftDegPerDay(rot: SolRotation, latDeg: number): number {
  return siderealRateDegPerDay(rot, latDeg) - rot.carringtonSidereal;
}

/**
 * Days for a feature at a latitude to come back to the same Earth-facing
 * longitude: the synodic period, 360 / (sidereal rate - Earth's mean motion).
 *
 * The Carrington frame's own 27.2753 days is NOT the equator's. Carrington set
 * his rate from low-latitude spots, and the fitted law returns it at about 19
 * degrees; the equator is faster, near 26.7 days, and 60 degrees is near 31.3.
 */
export function synodicPeriodDays(rot: SolRotation, latDeg: number): number {
  return 360 / (siderealRateDegPerDay(rot, latDeg) - rot.earthMeanMotion);
}

/** Carrington longitude of the sub-Earth point at a JD, degrees. */
export function subEarthLongitudeDeg(rot: SolRotation, jd: number): number {
  const l = rot.l0RefDeg + rot.l0RatePerDay * (jd - rot.l0RefJd);
  return ((l % 360) + 360) % 360;
}

/**
 * Angular radius of the circle holding an area, degrees.
 *
 * The record's areas are millionths of the solar HEMISPHERE, whose area is
 * 2 pi R^2, and a cap of angular radius t covers 2 pi R^2 (1 - cos t).
 */
export function areaToRadiusDeg(areaMillionths: number): number {
  const f = Math.min(1, Math.max(0, areaMillionths / 1e6));
  return Math.acos(1 - f) / DEG;
}

/** Limb darkening on the bare photosphere, 1 at disc centre. */
export function limbDarkening(mu: number): number {
  const m = Math.min(1, Math.max(0, mu));
  return Math.max(0, 1 - LIMB_DARKENING_U1 * (1 - m) - LIMB_DARKENING_U2 * (1 - m) * (1 - m));
}

const FACULA_NORM = Math.pow(FACULA_MU_PEAK, 3) * Math.pow(1 - FACULA_MU_PEAK, 7);

/** Facular brightening against mu, 0 at disc centre and 1 at the peak. */
export function faculaContrast(mu: number): number {
  const m = Math.min(1, Math.max(0, mu));
  return (Math.pow(m, 3) * Math.pow(1 - m, 7)) / FACULA_NORM;
}

/**
 * Which granulation generation is on screen and how far into it.
 *
 * Granules live about ten minutes and the field replaces itself continuously,
 * so the shader stage wants two noise fields and a blend between them rather
 * than one that snaps. `generation` is the integer to seed with and `blend`
 * runs 0 to 1 across that cell lifetime.
 */
export function granulation(jd: number): { generation: number; blend: number } {
  const t = (jd * 86400) / GRANULE_LIFETIME_SECONDS;
  const g = Math.floor(t);
  return { generation: g, blend: t - g };
}

/** Where a heliographic point lands on the disc an observer sees. */
export interface SolDiskView {
  /** Cosine of the angle from disc centre; at or under 0 is the far side. */
  readonly mu: number;
  /** Disc coordinates in solar radii, +x toward solar west, +y toward north. */
  readonly x: number;
  readonly y: number;
  readonly onDisk: boolean;
}

/**
 * A heliographic lat/lon as seen from a sub-observer point.
 *
 * Body-fixed axes are the ones every lat/lon in this repo is written in and
 * the ones `sol.ts`'s shader reconstructs: x at longitude 0, y north, longitude
 * east toward -z.
 */
export function diskView(latDeg: number, lonDeg: number,
  subLatDeg: number, subLonDeg: number): SolDiskView {
  const p = unit(latDeg, lonDeg);
  const s = unit(subLatDeg, subLonDeg);
  const mu = p[0] * s[0] + p[1] * s[1] + p[2] * s[2];
  // North with the sub-observer direction taken out of it, then west as their
  // cross product, which is the direction a feature drifts toward with time.
  let nx = -s[1] * s[0];
  let ny = 1 - s[1] * s[1];
  let nz = -s[1] * s[2];
  const nl = Math.hypot(nx, ny, nz) || 1;
  nx /= nl; ny /= nl; nz /= nl;
  const wx = ny * s[2] - nz * s[1];
  const wy = nz * s[0] - nx * s[2];
  const wz = nx * s[1] - ny * s[0];
  return {
    mu,
    x: p[0] * wx + p[1] * wy + p[2] * wz,
    y: p[0] * nx + p[1] * ny + p[2] * nz,
    onDisk: mu > 0,
  };
}

function unit(latDeg: number, lonDeg: number): [number, number, number] {
  const la = latDeg * DEG;
  const lo = lonDeg * DEG;
  const c = Math.cos(la);
  return [c * Math.cos(lo), Math.sin(la), -c * Math.sin(lo)];
}

// --- reading the artifact ----------------------------------------------------

function num(v: unknown, what: string): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    throw new Error(`sol-regions.json: ${what} is not a number`);
  }
  return v;
}

function arr(v: unknown, what: string): unknown[] {
  if (!Array.isArray(v)) throw new Error(`sol-regions.json: ${what} is not an array`);
  return v;
}

function obj(v: unknown, what: string): Record<string, unknown> {
  if (typeof v !== 'object' || v === null) {
    throw new Error(`sol-regions.json: ${what} is not an object`);
  }
  return v as Record<string, unknown>;
}

function numbers(v: unknown, what: string): number[] {
  return arr(v, what).map((x, i) => num(x, `${what}[${i}]`));
}

/**
 * Read the baked catalogue. Takes the PARSED json for each file, so this
 * stays a leaf.
 *
 * `modelJson` is `sol-model.json`, always required. `recordJson` is
 * `sol-regions.json`; pass it once it has landed, or leave it out (or pass
 * `null`) to get a catalogue that answers every query with the seeded model
 * alone - `recordLoaded` is false and `regions` is empty, which is exactly
 * what opening Sol before the record fetch resolves needs to draw.
 */
export function parseSolCatalogue(modelJson: unknown, recordJson: unknown = null): SolCatalogue {
  const doc = obj(modelJson, 'root');
  if (doc['schema'] !== 'fxciv-sol-model-1') {
    throw new Error(`sol-model.json: schema is ${String(doc['schema'])}, not fxciv-sol-model-1`);
  }
  const epoch = num(doc['epoch_jd'], 'epoch_jd');
  const tick = num(doc['time_unit_days'], 'time_unit_days');
  const rotRaw = obj(doc['rotation'], 'rotation');
  const rotation: SolRotation = {
    a: num(rotRaw['a'], 'rotation.a'),
    b: num(rotRaw['b'], 'rotation.b'),
    carringtonSidereal: num(rotRaw['carrington_sidereal_deg_per_day'], 'rotation.carrington'),
    carringtonSynodicDays: num(rotRaw['carrington_synodic_days'], 'rotation.synodic'),
    earthMeanMotion: num(rotRaw['earth_mean_motion_deg_per_day'], 'rotation.earth'),
    l0RefJd: epoch + num(rotRaw['l0_ref_jd'], 'rotation.l0_ref_jd'),
    l0RefDeg: num(rotRaw['l0_ref_deg'], 'rotation.l0_ref_deg'),
    l0RatePerDay: num(rotRaw['l0_deg_per_day'], 'rotation.l0_deg_per_day'),
    umbraFraction: num(rotRaw['umbra_fraction'], 'rotation.umbra_fraction'),
  };
  const mRaw = obj(doc['model'], 'model');
  const areaLn = obj(mRaw['peak_area_ln'], 'model.peak_area_ln');
  const lifeLn = obj(mRaw['lifetime_ln'], 'model.lifetime_ln');
  const perCycle = obj(mRaw['regions_per_cycle'], 'model.regions_per_cycle');
  const model: SolCycleModel = {
    minimaJd: numbers(mRaw['cycle_minima_jd'], 'model.cycle_minima_jd').map((d) => epoch + d),
    firstCycleNumber: num(mRaw['first_cycle_number'], 'model.first_cycle_number'),
    meanCycleDays: num(mRaw['mean_cycle_days'], 'model.mean_cycle_days'),
    regionsPerCycleMean: num(perCycle['mean'], 'model.regions_per_cycle.mean'),
    regionsPerCycleSd: num(perCycle['sd'], 'model.regions_per_cycle.sd'),
    emergenceByPhase: numbers(mRaw['emergence_by_phase'], 'model.emergence_by_phase'),
    butterflyLatDeg: numbers(mRaw['butterfly_lat_deg'], 'model.butterfly_lat_deg'),
    butterflyLatSdDeg: numbers(mRaw['butterfly_lat_sd_deg'], 'model.butterfly_lat_sd_deg'),
    peakAreaLnMu: num(areaLn['mu'], 'model.peak_area_ln.mu'),
    peakAreaLnSd: num(areaLn['sd'], 'model.peak_area_ln.sd'),
    peakAreaMax: num(areaLn['max'], 'model.peak_area_ln.max'),
    lifetimeLnA: num(lifeLn['a'], 'model.lifetime_ln.a'),
    lifetimeLnB: num(lifeLn['b'], 'model.lifetime_ln.b'),
    lifetimeLnSd: num(lifeLn['sd'], 'model.lifetime_ln.sd'),
    profile: numbers(mRaw['profile'], 'model.profile'),
  };
  const srcRaw = obj(doc['source'], 'source');
  const source: SolSource = {
    name: String(srcRaw['name']),
    url: String(srcRaw['url']),
    rights: String(srcRaw['rights']),
    range: String(srcRaw['range']),
    retrieved: String(srcRaw['retrieved']),
  };
  const recRaw = obj(doc['record'], 'record');
  const unobservedJd = new Set<number>(
    numbers(recRaw['unobserved'], 'record.unobserved').map((d) => epoch + d));
  const maxLifeDaysBase = num(doc['max_life_days'], 'max_life_days');

  let regions: CatalogueRegion[] = [];
  let sampleJd = new Float64Array(0);
  let sampleArea = new Float64Array(0);
  let recordLoaded = false;
  if (recordJson !== null) {
    const rdoc = obj(recordJson, 'root');
    if (rdoc['schema'] !== 'fxciv-sol-record-1') {
      throw new Error(`sol-regions.json: schema is ${String(rdoc['schema'])}, not fxciv-sol-record-1`);
    }
    const rEpoch = num(rdoc['epoch_jd'], 'record epoch_jd');
    const rTick = num(rdoc['time_unit_days'], 'record time_unit_days');
    const classes = arr(rdoc['classes'], 'classes').map((c) => String(c));
    const dts = numbers(rdoc['sample_dt'], 'sample_dt');
    const areas = numbers(rdoc['sample_area'], 'sample_area');
    const rows = arr(rdoc['regions'], 'regions');
    const built: CatalogueRegion[] = [];
    sampleJd = new Float64Array(dts.length);
    sampleArea = new Float64Array(areas.length);
    for (let i = 0; i < rows.length; i += 1) {
      const r = numbers(rows[i], `regions[${i}]`);
      const t0 = rEpoch + (r[0] ?? 0) * rTick;
      const life = (r[2] ?? 0) * rTick;
      const sample0 = r[9] ?? 0;
      const count = r[10] ?? 0;
      let at = t0;
      for (let k = 0; k < count; k += 1) {
        at += (dts[sample0 + k] ?? 0) * rTick;
        sampleJd[sample0 + k] = at;
        sampleArea[sample0 + k] = areas[sample0 + k] ?? 0;
      }
      const year = r[8] ?? 0;
      built.push({
        jd0: t0,
        jdPeak: t0 + (r[1] ?? 0) * rTick,
        jd1: t0 + life,
        latDeg: (r[3] ?? 0) / 10,
        lonDeg: (r[4] ?? 0) / 10,
        peakArea: r[5] ?? 0,
        classCode: classes[r[6] ?? 0] ?? '',
        number: r[7] ?? 0,
        year,
        sample0,
        samples: count,
      });
    }
    regions = built;
    recordLoaded = true;
  }

  return {
    source,
    rotation,
    model,
    recordStartJd: epoch + num(recRaw['start'], 'record.start'),
    recordEndJd: epoch + num(recRaw['end'], 'record.end'),
    unobservedJd,
    regions,
    sampleJd,
    sampleArea,
    maxLifeDays: maxLifeDaysBase + 2 * EDGE_RAMP_DAYS,
    recordLoaded,
  };
}

// --- the seeded model --------------------------------------------------------

/** mulberry32: 32-bit integer state, so every run gives the same stream. */
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** One standard normal from two uniforms, Box-Muller. */
function gauss(r: () => number): number {
  const u = Math.max(1e-12, r());
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * r());
}

/** Cycle index the JD falls in; negative before the record's first minimum. */
function cycleIndexAt(cat: SolCatalogue, jd: number): number {
  const m = cat.model.minimaJd;
  const first = m[0] ?? 0;
  const last = m[m.length - 1] ?? 0;
  if (jd < first) return Math.floor((jd - first) / cat.model.meanCycleDays);
  if (jd >= last) return m.length - 1 + Math.floor((jd - last) / cat.model.meanCycleDays);
  for (let i = m.length - 2; i >= 0; i -= 1) if (jd >= (m[i] ?? 0)) return i;
  return 0;
}

function cycleStartJd(cat: SolCatalogue, index: number): number {
  const m = cat.model.minimaJd;
  if (index >= 0 && index < m.length) return m[index] ?? 0;
  if (index < 0) return (m[0] ?? 0) + index * cat.model.meanCycleDays;
  return (m[m.length - 1] ?? 0) + (index - (m.length - 1)) * cat.model.meanCycleDays;
}

function cycleLengthDays(cat: SolCatalogue, index: number): number {
  return cycleStartJd(cat, index + 1) - cycleStartJd(cat, index);
}

/** A modelled region, in the same shape as a catalogue one. */
interface ModelRegion extends CatalogueRegion {
  readonly lifetimeDays: number;
}

const modelCache = new WeakMap<SolCatalogue, Map<number, readonly ModelRegion[]>>();

/**
 * One invented cycle's worth of regions, drawn from the record's own fits.
 *
 * Seeded by the CYCLE NUMBER alone: the whole cycle is generated at once and
 * then queried exactly like the measured catalogue, which is what stops a date
 * a second later from being a fresh roll. A region emerging inside the record
 * is dropped, so the measured years keep their own regions and the density
 * either side of the record's last day is continuous.
 */
function modelCycle(cat: SolCatalogue, index: number): readonly ModelRegion[] {
  let per = modelCache.get(cat);
  if (per === undefined) { per = new Map(); modelCache.set(cat, per); }
  const hit = per.get(index);
  if (hit !== undefined) return hit;

  const m = cat.model;
  const start = cycleStartJd(cat, index);
  const length = cycleLengthDays(cat, index);
  const number = m.firstCycleNumber + index;
  const r = rng(Math.imul(number + 4096, 2654435761) ^ 0x50130000);
  const amp = Math.min(2.2, Math.max(0.25,
    1 + (m.regionsPerCycleSd / m.regionsPerCycleMean) * gauss(r)));
  const count = Math.max(1, Math.round(m.regionsPerCycleMean * amp));

  // Inverse CDF of the emergence-rate histogram, linear inside a bin.
  const cdf: number[] = [];
  let acc = 0;
  for (const p of m.emergenceByPhase) { acc += p; cdf.push(acc); }
  const total = acc || 1;

  const out: ModelRegion[] = [];
  for (let i = 0; i < count; i += 1) {
    const u = r() * total;
    let bin = 0;
    while (bin < cdf.length - 1 && (cdf[bin] ?? 0) < u) bin += 1;
    const lo = bin === 0 ? 0 : (cdf[bin - 1] ?? 0);
    const hi = cdf[bin] ?? total;
    const within = hi > lo ? (u - lo) / (hi - lo) : 0;
    const phase = (bin + within) / m.emergenceByPhase.length;
    const jd0 = start + phase * length;

    const latMean = m.butterflyLatDeg[bin] ?? 15;
    const latSd = m.butterflyLatSdDeg[bin] ?? 7;
    const lat = Math.min(42, Math.max(1.5, Math.abs(latMean + latSd * gauss(r))))
      * (r() < 0.5 ? -1 : 1);
    const lon = r() * 360;
    const peak = Math.min(m.peakAreaMax,
      Math.max(1, Math.exp(m.peakAreaLnMu + m.peakAreaLnSd * gauss(r))));
    const life = Math.min(cat.maxLifeDays, Math.max(0.6, Math.exp(
      m.lifetimeLnA + m.lifetimeLnB * Math.log(peak) + m.lifetimeLnSd * gauss(r))));
    // Only excluded once the record itself is in hand: before that, the
    // measured rows this would defer to do not exist yet, and skipping the
    // synthesis here would draw a blank disc instead of a modelled one.
    if (cat.recordLoaded && jd0 >= cat.recordStartJd && jd0 <= cat.recordEndJd) continue;
    out.push({
      jd0,
      jdPeak: jd0 + life * peakOffsetFraction(m),
      jd1: jd0 + life,
      latDeg: lat,
      lonDeg: lon,
      peakArea: peak,
      classCode: '',
      number: i,
      year: number,
      sample0: -1,
      samples: 0,
      lifetimeDays: life,
    });
  }
  out.sort((x, y) => x.jd0 - y.jd0 || x.number - y.number);
  per.set(index, out);
  return out;
}

/** Where the record's own mean profile peaks, as a fraction of a life. */
function peakOffsetFraction(m: SolCycleModel): number {
  let best = 0;
  let at = 0;
  for (let i = 0; i < m.profile.length; i += 1) {
    const v = m.profile[i] ?? 0;
    if (v > best) { best = v; at = i; }
  }
  return m.profile.length > 1 ? at / (m.profile.length - 1) : 0.5;
}

/** The mean profile sampled at a normalised time, linear between its points. */
function profileAt(m: SolCycleModel, u: number): number {
  if (u <= 0 || u >= 1) return 0;
  const n = m.profile.length;
  if (n < 2) return 1;
  const x = u * (n - 1);
  const i = Math.min(n - 2, Math.floor(x));
  const f = x - i;
  return (m.profile[i] ?? 0) * (1 - f) + (m.profile[i + 1] ?? 0) * f;
}

// --- the query ---------------------------------------------------------------

/**
 * A measured region's area at an instant, from its own daily rows.
 *
 * Linear between rows, and ramped to nothing over half a day outside the first
 * and last, because the record is daily samples and a step from zero to a full
 * spot group is exactly the pop this is here to avoid.
 */
function measuredArea(cat: SolCatalogue, reg: CatalogueRegion, jd: number): number {
  const n = reg.samples;
  if (n === 0) return 0;
  const first = cat.sampleJd[reg.sample0] ?? reg.jd0;
  const last = cat.sampleJd[reg.sample0 + n - 1] ?? reg.jd1;
  const a0 = cat.sampleArea[reg.sample0] ?? 0;
  const a1 = cat.sampleArea[reg.sample0 + n - 1] ?? 0;
  if (jd < first) {
    const f = (jd - (first - EDGE_RAMP_DAYS)) / EDGE_RAMP_DAYS;
    return f <= 0 ? 0 : a0 * f;
  }
  if (jd > last) {
    const f = ((last + EDGE_RAMP_DAYS) - jd) / EDGE_RAMP_DAYS;
    return f <= 0 ? 0 : a1 * f;
  }
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if ((cat.sampleJd[reg.sample0 + mid] ?? 0) <= jd) lo = mid; else hi = mid;
  }
  const ta = cat.sampleJd[reg.sample0 + lo] ?? 0;
  const tb = cat.sampleJd[reg.sample0 + hi] ?? ta + 1;
  const aa = cat.sampleArea[reg.sample0 + lo] ?? 0;
  const ab = cat.sampleArea[reg.sample0 + hi] ?? 0;
  const f = tb > ta ? (jd - ta) / (tb - ta) : 0;
  return aa * (1 - f) + ab * f;
}

function firstAtOrAfter(regions: readonly CatalogueRegion[], jd: number): number {
  let lo = 0;
  let hi = regions.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((regions[mid]?.jd0 ?? 0) < jd) lo = mid + 1; else hi = mid;
  }
  return lo;
}

function toRegion(cat: SolCatalogue, reg: CatalogueRegion, jd: number,
  area: number, measured: boolean): SolRegion {
  const drift = carringtonDriftDegPerDay(cat.rotation, reg.latDeg);
  const lon = (((reg.lonDeg + drift * (jd - reg.jdPeak)) % 360) + 360) % 360;
  const radius = areaToRadiusDeg(area);
  // `year` carries the cycle number on a modelled region, not a calendar year.
  const series = reg.year >= 1977 ? 'noaa' : 'rgo';
  return {
    id: measured ? `${series}-${reg.year}-${reg.number}` : `model-${reg.year}-${reg.number}`,
    measured,
    number: reg.number,
    latDeg: reg.latDeg,
    lonDeg: lon,
    areaMillionths: area,
    peakAreaMillionths: reg.peakArea,
    radiusDeg: radius,
    umbraRadiusDeg: areaToRadiusDeg(area * cat.rotation.umbraFraction),
    classCode: reg.classCode,
    ageDays: jd - reg.jd0,
    lifetimeDays: reg.jd1 - reg.jd0,
  };
}

/** Day index of a JD as the artifact counts them: noon to noon. */
function noonJd(jd: number): number {
  return Math.floor(jd + 0.5);
}

/**
 * Every active region on the whole sphere at a Julian date.
 *
 * Measured where the date falls inside the record, modelled outside it, and
 * never both for the same region. The list is sorted by id, so the same date
 * twice gives a byte-identical list.
 */
export function solPhotosphereAt(cat: SolCatalogue, jd: number): SolPhotosphere {
  const out: SolRegion[] = [];
  const inRecord = jd >= cat.recordStartJd - 0.5 && jd <= cat.recordEndJd + 0.5;

  let i = firstAtOrAfter(cat.regions, jd - cat.maxLifeDays);
  for (; i < cat.regions.length; i += 1) {
    const reg = cat.regions[i];
    if (reg === undefined) break;
    if (reg.jd0 - EDGE_RAMP_DAYS > jd) break;
    if (reg.jd1 + EDGE_RAMP_DAYS < jd) continue;
    const area = measuredArea(cat, reg, jd);
    if (area <= 0) continue;
    out.push(toRegion(cat, reg, jd, area, true));
  }

  const index = cycleIndexAt(cat, jd);
  for (const ci of [index - 1, index]) {
    for (const reg of modelCycle(cat, ci)) {
      if (reg.jd0 > jd || reg.jd1 < jd) continue;
      const area = reg.peakArea * profileAt(cat.model, (jd - reg.jd0) / reg.lifetimeDays);
      if (area <= 0.5) continue;
      out.push(toRegion(cat, reg, jd, area, false));
    }
  }

  out.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  let measuredCount = 0;
  let artificialArea = 0;
  let totalArea = 0;
  for (const r of out) {
    totalArea += r.areaMillionths;
    if (r.measured) measuredCount += 1; else artificialArea += r.areaMillionths;
  }
  const length = cycleLengthDays(cat, index);
  const start = cycleStartJd(cat, index);
  const cycle: SolCycleState = {
    number: cat.model.firstCycleNumber + index,
    phase: length > 0 ? (jd - start) / length : 0,
    startJd: start,
    lengthDays: length,
    measured: index >= 0 && index < cat.model.minimaJd.length - 1,
  };
  const observed = !inRecord || !cat.unobservedJd.has(noonJd(jd));
  return {
    jd,
    regions: out,
    inRecord,
    observed,
    measuredCount,
    artificialCount: out.length - measuredCount,
    artificialAreaFraction: totalArea > 0 ? artificialArea / totalArea : 0,
    totalAreaMillionths: totalArea,
    subEarthLonDeg: subEarthLongitudeDeg(cat.rotation, jd),
    cycle,
    note: noteFor(cat, out.length - measuredCount, out.length, inRecord, observed),
  };
}

/**
 * The words under the Layers note.
 *
 * The standing rule for artificial content: it sits behind the Layers toggle
 * and carries a note, and nothing modelled is dressed as measured. So the note
 * names the record, its end, and what is standing in past it.
 */
function noteFor(cat: SolCatalogue, artificial: number, total: number,
  inRecord: boolean, observed: boolean): string {
  const range = cat.source.range;
  if (!inRecord) {
    return `The sunspot record runs ${range}. This date is outside it, so every `
      + `region here is modelled from that record's own cycle statistics, not observed.`;
  }
  if (!cat.recordLoaded) {
    return `The sunspot record for ${range} is still loading. Until it lands, every `
      + `region here is modelled from the record's own cycle statistics, not observed.`;
  }
  if (!observed) {
    return `No sunspot observation exists for this day. The regions here are the `
      + `record's own, carried from the days either side.`;
  }
  if (artificial > 0) {
    return `${artificial} of ${total} regions here are modelled: the sunspot record `
      + `ends with ${range.slice(-10)}.`;
  }
  return '';
}
